import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { GET_NUMBER_CHANNEL_CHOICES } from "../../graphql/queries/numChannelsChoices";
import Loader from "../../components/loader/loader";
import { CREATE_TOWER_MUTATION } from "../../graphql/mutations/createTowerMutation";
import { UPDATE_TOWER_MUTATION } from "../../graphql/mutations/updateTowerMutation";
import { toastFunction, errorToastFunction } from "../../utils/toast";
import FloatingLabelInput from "../FloatingLabelInput/FloatingLabelInput";
import SingleSelect from "../single-select/single-select";
import DisplaySelect from "../display-select/display-select";

const TowerModal = ({ onCancel, tower = null }) => {
  const isEditMode = !!tower;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: tower
      ? {
          zonke_id: tower.zonkeId,
          w3w: tower.w3w || "",
          vrm_portal_id: tower.vrmPortalId || "",
          install_date: tower.installDate,
          number_channels: {
            value: tower.numberChannels,
            label: tower.numberChannels,
          },
          port: tower.port || "",
        }
      : {},
  });

  const [errorMessage, setErrorMessage] = useState(null);
  const [choicesData, setChoicesData] = useState([]);

  const [createTower, { loading: createLoading }] = useMutation(
    CREATE_TOWER_MUTATION
  );
  const [updateTower, { loading: updateLoading }] = useMutation(
    UPDATE_TOWER_MUTATION
  );

  const onSubmit = async (data) => {
    const towerData = {
      zonke_id: data.zonke_id,
      w3w: data.w3w,
      vrm_portal_id: data.vrm_portal_id,
      install_date: data.install_date,
      number_channels: parseInt(data.number_channels.value, 10),
      port: data.port ? parseInt(data.port, 10) : null,
    };

    try {
      setErrorMessage("");
      if (isEditMode) {
        await updateTower({
          variables: {
            towerData,
            tower_id: parseInt(tower.id),
          },
        });
        toastFunction(
          `Successfully updated tower ${towerData.zonke_id}`,
          "success",
          "tower-update-success"
        );
      } else {
        const { data } = await createTower({
          variables: { towerData: towerData },
        });
        toastFunction(
          `Successfully created tower with zonke id ${data.createTower.zonke_id}.`,
          "success",
          "tower-create-success"
        );
      }
      onCancel();
      reset();
    } catch (error) {
      console.error("Tower mutation error:", error);

      let displayError = `Failed to ${isEditMode ? "update" : "create"} tower. Please try again.`;

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0].message;

        if (graphQLError.includes("Duplicate entry")) {
          displayError = `A tower with this Zonke ID already exists.`;
        } else {
          displayError = graphQLError;
        }
      } else if (error.networkError) {
        displayError = `Network error: ${error.networkError.message}`;
      } else if (error.message) {
        displayError = error.message;
      }

      setErrorMessage(displayError);
      errorToastFunction(
        displayError,
        `tower-${isEditMode ? "update" : "create"}-error`
      );
    }
  };

  const getNumberOfChoices = useQuery(GET_NUMBER_CHANNEL_CHOICES, {
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (getNumberOfChoices.data) {
      const mappedData = getNumberOfChoices.data.numberChannels.map((data) => {
        return {
          value: data,
          label: data,
        };
      });
      setChoicesData(mappedData);
    }
  }, [getNumberOfChoices]);

  if (createLoading || updateLoading || getNumberOfChoices.loading) {
    return <Loader sizeClass={"size--medium"} />;
  }

  if (getNumberOfChoices.error) {
    return (
      <div className="basic-modal">
        <div className="basic-modal__header">
          <h3>Error Loading Data</h3>
        </div>
        <div className="basic-modal__body">
          <div
            className="input-container__error"
            style={{
              padding: "20px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
            }}
          >
            {getNumberOfChoices.error?.message ||
              "Failed to load channel choices. Please try again."}
          </div>
          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="basic-modal">
      <div className="basic-modal__header">
        <h3>{isEditMode ? "Edit Tower" : "Add Tower"}</h3>
      </div>
      <div className="basic-modal__body">
        {errorMessage && (
          <div
            className="input-container__error"
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef5350",
              borderRadius: "4px",
              color: "#c62828",
              fontSize: "14px",
              lineHeight: "1.5",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {errorMessage}
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="basic-modal__form">
          <FloatingLabelInput
            type={"string"}
            className={""}
            disabled={false}
            name={"zonke_id"}
            register={register}
            rules={{
              required: "The zonke id field is required.",
            }}
            placeholder={"Zonke ID"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"vrm_portal_id"}
            register={register}
            rules={{ required: false }}
            placeholder={"VRM Portal ID"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"text"}
            className={""}
            disabled={false}
            name={"w3w"}
            register={register}
            rules={{ required: false }}
            placeholder={"W3W Address"}
            errors={errors}
          />

          <FloatingLabelInput
            type={"date"}
            className={""}
            disabled={false}
            name={"install_date"}
            register={register}
            rules={{ required: "The Install Date field is required." }}
            placeholder={"Install Date"}
            errors={errors}
          />

          <div className="input-container">
            <DisplaySelect
              placeholder={"Number of Channels"}
              name={"number_channels"}
              errors={errors}
            >
              <Controller
                name={"number_channels"}
                rules={{
                  required: true,
                }}
                control={control}
                render={({ field }) => (
                  <SingleSelect field={field} options={choicesData} />
                )}
              />
            </DisplaySelect>
          </div>

          <FloatingLabelInput
            type={"number"}
            className={""}
            disabled={false}
            name={"port"}
            register={register}
            rules={{ required: false }}
            placeholder={"Port"}
            errors={errors}
          />

          <div className="basic-modal__footer">
            <button onClick={onCancel} className="button button--secondary">
              Cancel
            </button>
            <button type="submit" className="button button--primary">
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TowerModal;
